import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Space, Table, Tag } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const PmvReliabilityFailuresPage: React.FC = () => {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['pmv-reliability-failures'],
    queryFn: () => pmvService.listReliabilityFailures({ limit: 100 }),
  });

  const items = q.data?.items ?? [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Normalized Failures"
        subtitle="Projection from breakdown occurrence — breakdown remains authority"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/reliability/control-tower')}>
              Control Tower
            </Button>
            <Button onClick={() => navigate('/asset/pmv/reliability')}>Health Register</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/attention')}>Attention</Button>
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Normalized failure rows are projections — breakdown occurrence remains the authority of record."
      />
      {!q.isLoading && items.length === 0 ? (
        <Empty description="No normalized failures recorded" />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={q.isLoading}
          dataSource={items}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1100 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Equipment',
              dataIndex: 'equipment_label',
              width: 200,
              ellipsis: true,
              render: (v, r) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}`)}
                  >
                    {v || 'Equipment'}
                  </Button>
                </div>
              ),
            },
            {
              title: 'System',
              dataIndex: 'failure_system',
              width: 140,
              ellipsis: true,
              render: (v) => v || '—',
            },
            {
              title: 'Mode',
              dataIndex: 'failure_mode',
              width: 160,
              ellipsis: true,
              render: (v) => v || '—',
            },
            {
              title: 'Cause',
              dataIndex: 'cause_state',
              width: 120,
              render: (v) => <Tag>{v || 'UNKNOWN'}</Tag>,
            },
            {
              title: 'Source',
              dataIndex: 'source_type',
              width: 120,
              ellipsis: true,
            },
            {
              title: 'Occurred',
              dataIndex: 'occurred_at',
              width: 170,
              render: (v) => (v ? new Date(v).toLocaleString() : '—'),
            },
            {
              title: 'MTBF eligible',
              dataIndex: 'qualifies_for_mtbf',
              width: 110,
              render: (v) => (v ? 'Yes' : 'No'),
            },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvReliabilityFailuresPage;
